import { computed, ref } from 'vue'
import { translate } from '@/i18n/i18n.ts'
import { useAppFormService } from '@/libs/api/appform/app-form.service.ts'
import { useInsuranceStore } from '@/libs/api/insurance/insurance.store.ts'
import { useConnectStore } from '@/libs/api/connect/connect.store.ts'
import type {
  ProductOfferResponse,
  ProductSpecificationResponse
} from '@/libs/api/insurance/insurance.types.ts'
import { useUiStore } from '@/stores/ui.store.ts'
import type { ProductOfferDto } from '@/ts/types/insurance.dto.ts'
import type { LValidationResult } from '@/libs/validation/components/LValidationForm.vue'

export type TariffState = {
  deviceClassId?: string
  purchaseDate?: string
  purchasePrice?: string
}

export const useMiniVariantTariffState = () => {
  const appFormService = useAppFormService()

  const uiStore = useUiStore()
  const insuranceStore = useInsuranceStore()
  const connectStore = useConnectStore()

  const validationResult = ref<LValidationResult>({
    isValid: false,
    hasError: false,
    isPristine: true,
    isDirty: false
  })
  const tariffState = ref<TariffState>()

  // mini variant specific way to store product offer and specifications for selected device class
  const _currentOffer = ref<ProductOfferResponse | null>(null)
  const _currentProductSpec = ref<ProductSpecificationResponse | null>(null)

  const currentPrice = ref(0)

  // mini variant specific way to update the current offer
  const updateOffer = async (
    newData: TariffState | undefined,
    updatePrice: boolean = true
  ): Promise<ProductOfferResponse | undefined> => {
    if (!newData) return undefined
    const { deviceClassId } = newData

    if (deviceClassId) {
      const offer = getOffer(deviceClassId)
      setCurrentOffer(offer ?? null, updatePrice)
      return offer
    }
  }

  const currentProductSpec = computed(() => _currentProductSpec.value)

  const redirectUrl = computed(() => {
    const checkoutUrl = translate('checkout.target_url_bike')
    const offer = currentOffer.value

    return appFormService.buildPrefillUrl(checkoutUrl, {
      device_class_id: tariffState.value?.deviceClassId,
      product_id: offer?.id,
      device_purchase_price: tariffState.value?.purchasePrice,
      device_purchase_date: tariffState.value?.purchaseDate,
      partner: connectStore.dataAttributes?.brokerNumber,
      sc_sid: connectStore.dataAttributes?.partnerId,
      prefill: connectStore.dataAttributes?.prefill
    })
  })

  const labelOffer = computed(() => {
    const specPrice = _currentProductSpec.value?.price
    const offerPrice = currentOffer.value?.price_min

    if (specPrice) {
      return translate('product.protect_now_with_spec', specPrice.toString())
    }
    if (offerPrice) {
      return translate('product.protect_now_without_spec', offerPrice.toString())
    }

    return translate('product.protect_now')
  })

  const currentOffer = computed(() => {
    return _currentOffer.value
  })

  const currentOfferDto = computed(() => {
    const dto: ProductOfferDto = {
      content: _currentOffer.value?.content,
      documents: _currentOffer.value?.documents ?? [],
      price_groups: _currentOffer.value?.price_groups ?? {},
      title: _currentOffer.value?.title,
      price_min: currentOffer.value?.price_min
    }
    return dto
  })

  const canRedirect = computed(() => {
    return validationResult.value.isValid && !!currentProductSpec.value
  })

  async function fetchCurrentProductSpec() {
    const result = await insuranceStore.loadProductSpec({
      product_id: _currentOffer.value?.id,
      device_class_id: tariffState.value?.deviceClassId,
      purchase_date: tariffState.value?.purchaseDate,
      purchase_price: tariffState.value?.purchasePrice
    })
    setCurrentProductSpec(result)
  }

  function resetProductSpec() {
    _currentProductSpec.value = null
    updateCurrentPrice()
    uiStore.setLoading(false)
  }

  function updateCurrentPrice() {
    const currentProductSpecPrice = _currentProductSpec.value?.price
    const currentOfferPrice = _currentOffer.value?.price_min
    if (currentProductSpecPrice) {
      currentPrice.value = currentProductSpecPrice
      return
    }
    if (currentOfferPrice) {
      currentPrice.value = currentOfferPrice
      return
    }
  }

  function setCurrentProductSpec(value: ProductSpecificationResponse) {
    _currentProductSpec.value = value
  }

  function getOffer(deviceClassId: string) {
    const offers = insuranceStore.getDeviceClassOffers(deviceClassId)
    const offer = offers.find((currentOffer) => currentOffer.suggest) ?? offers[0]

    if (!offer) {
      console.warn(`No offer found for device class ID: ${deviceClassId}`)
    }

    return offer
  }

  function setCurrentOffer(value: ProductOfferResponse | null, updatePrice: boolean = true) {
    _currentOffer.value = value
    if (updatePrice) {
      updateCurrentPrice()
    }
  }

  return {
    tariffState,
    currentPrice,
    currentProductSpec,
    currentOffer,
    labelOffer,
    redirectUrl,
    updateOffer,
    fetchCurrentProductSpec,
    resetProductSpec,
    updateCurrentPrice,
    canRedirect,
    setCurrentProductSpec,
    currentOfferDto,
    validationResult
  }
}
